import { useForm } from 'react-hook-form';
import { useRef, useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';

import { BillingSchema, PlatformBillingSchema } from '../types/order-form.types';

import type { BillingSchemaType } from '../types/order-form.types';

export function useBillingForm() {
  // Default 'platform' — la mayoría de pedidos externos se facturan por plataforma. En modo
  // edición el hook de preload sobrescribe el valor según el pedido cargado.
  const [billingMethod, setBillingMethodState] = useState<'email' | 'platform'>('platform');
  const [formMode, setFormMode] = useState<'email' | 'platform'>('platform');

  // Valores con los que sembrar el formulario la próxima vez que cambie billingMethod (los
  // datos de facturación de un pedido ya existente, en modo edición). El efecto de más abajo
  // resetea el formulario a los defaults de cada modo en cuanto billingMethod cambia; el preload
  // hace sus billingMethods.setValue(...) en el mismo tick que llama a setBillingMethod, pero ese
  // reset se dispara en el render siguiente (cuando billingMethod ya cambió), así que sin esto
  // pisaba los valores recién cargados y dejaba el formulario de facturación vacío.
  const preloadValuesRef = useRef<Partial<BillingSchemaType> | null>(null);

  const resolver =
    formMode === 'email'
      ? zodResolver(BillingSchema)
      : (zodResolver(PlatformBillingSchema) as any);

  const billingMethods = useForm<BillingSchemaType>({
    resolver,
    defaultValues: {
      customerId: undefined,
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      nif: '',
      country: '',
      city: '',
      postalCode: '',
      address: '',
      platformId: undefined,
    },
  });

  const setBillingMethod = (
    method: 'email' | 'platform',
    preloadValues?: Partial<BillingSchemaType>
  ) => {
    preloadValuesRef.current = preloadValues ?? null;
    setBillingMethodState(method);
  };

  const validateBillingForm = async () => {
    if (formMode === 'email') {
      return billingMethods.trigger();
    }
    return billingMethods.trigger('platformId');
  };

  // Sync resolver with billing method
  useEffect(() => {
    setFormMode(billingMethod);

    const preloadValues = preloadValuesRef.current;
    preloadValuesRef.current = null;

    if (billingMethod === 'platform') {
      const platformId = preloadValues?.platformId ?? billingMethods.getValues('platformId');
      billingMethods.reset({ platformId }, { keepErrors: false });
    } else {
      billingMethods.reset(
        {
          customerId: undefined,
          firstName: '',
          lastName: '',
          company: '',
          email: '',
          phone: '',
          nif: '',
          country: '',
          city: '',
          postalCode: '',
          address: '',
          platformId: undefined,
          ...preloadValues,
        },
        { keepErrors: false }
      );
    }
  }, [billingMethod, billingMethods]);

  return {
    billingMethod,
    setBillingMethod,
    billingMethods,
    validateBillingForm,
  };
}
